// ./src/utils/api/ExpenseTagService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const EXPENSE_TAG_FIELDS = `
  id
  tagName
  tagColor
  status
  version
  createdBy
  updatedBy
  createdAt
  updatedAt
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const EXPENSE_TAG_LIST_QUERY = gql`
  query ExpenseTagList(
    $filter: ExpenseTagFilter
    $params: PaginationAndSorting
  ) {
    expenseTagList(filter: $filter, params: $params) {
      code
      data {
        ${EXPENSE_TAG_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_TAG_GET_QUERY = gql`
  query ExpenseTagGet($id: ID) {
    expenseTagGet(id: $id) {
      code
      data {
        ${EXPENSE_TAG_FIELDS}
        userCreated {
          id
          firstName
          lastName
        }
        userUpdated {
          id
          firstName
          lastName
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_TAG_GET_MANY_QUERY = gql`
  query ExpenseTagGetMany($ids: [ID]) {
    expenseTagGetMany(ids: $ids) {
      code
      data {
        ${EXPENSE_TAG_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const EXPENSE_TAG_CREATE_MUTATION = gql`
  mutation ExpenseTagCreate($params: ExpenseTagInput) {
    expenseTagCreate(params: $params) {
      code
      data {
        ${EXPENSE_TAG_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_TAG_UPDATE_MUTATION = gql`
  mutation ExpenseTagUpdate(
    $where: ExpenseTagWhereInput
    $params: ExpenseTagInput
  ) {
    expenseTagUpdate(where: $where, params: $params) {
      code
      data {
        ${EXPENSE_TAG_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_TAG_REMOVE_MUTATION = gql`
  mutation ExpenseTagRemove($where: ExpenseTagWhereInput) {
    expenseTagRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_TAG_REMOVE_MANY_MUTATION = gql`
  mutation ExpenseTagRemoveMany($where: [ExpenseTagWhereInput]) {
    expenseTagRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface ExpenseTag {
  id?: string | undefined;
  tagName?: string | undefined;
  tagColor?: string | undefined;
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
}

export interface ExpenseTagInput {
  id?: string | null | undefined;
  tagName?: string | null | undefined;
  tagColor?: string | null | undefined;
  status?: number | null | undefined;
}

export interface ExpenseTagFilter {
  id?: string[] | null | undefined;
  tagName?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface ExpenseTagWhereInput {
  id?: string | undefined;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * ExpenseTag service that handles all expense tag-related API calls.
 * Expense tags allow users to tag and categorize their expenses with custom tags.
 * Supports CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class ExpenseTagService {
  /**
   * Get a list of expense tags with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: ExpenseTagFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_TAG_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single expense tag by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_TAG_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple expense tags by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_TAG_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new expense tag
   */
  static async create(params: ExpenseTagInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EXPENSE_TAG_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing expense tag
   */
  static async update(
    where: ExpenseTagWhereInput,
    params: ExpenseTagInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EXPENSE_TAG_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete an expense tag (soft delete)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EXPENSE_TAG_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple expense tags (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EXPENSE_TAG_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}